import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { auth } from "@excellent/ui/api/contracts/auth";

export type DurableAccountIntent = "background" | "review" | "workflow" | "invite";

type PendingAction = {
  id: number;
  intent: DurableAccountIntent;
  run: () => void | Promise<void>;
};

const INTENT_COPY: Record<DurableAccountIntent, { title: string; detail: string }> = {
  background: {
    title: "Keep observing with an account",
    detail: "One-time local discovery works without an account. An account is required before Excellent keeps an ongoing observation grant.",
  },
  review: {
    title: "Keep this review with an account",
    detail: "The local discovery can stay here without an account. Sign in before Excellent retains the review or turns it into a workflow.",
  },
  workflow: {
    title: "Save this workflow with an account",
    detail: "Local discovery remains available without an account. An account is required before Excellent retains a workflow or runs background work for it.",
  },
  invite: {
    title: "Sign in before inviting someone",
    detail: "Invites create shared access, so they require an authenticated account.",
  },
};

export function useDurabilityAccountBoundary() {
  const account = useApi(auth.me, {});
  const [pending, setPending] = useState<PendingAction | null>(null);
  const pendingRef = useRef<PendingAction | null>(null);
  const nextActionId = useRef(0);
  const resuming = useRef(false);
  const refreshing = useRef(false);
  const [refreshPending, setRefreshPending] = useState(false);
  const accountReady = !account.isLoading && !account.error && account.data != null;

  const resume = useCallback(async (action: PendingAction) => {
    if (resuming.current || pendingRef.current?.id !== action.id) return;
    pendingRef.current = null;
    resuming.current = true;
    setPending((current) => current?.id === action.id ? null : current);
    try {
      await action.run();
    } finally {
      resuming.current = false;
    }
  }, []);

  useEffect(() => {
    if (pending && accountReady && account.data?.authenticated === true) void resume(pending);
  }, [account.data?.authenticated, accountReady, pending, resume]);

  const refresh = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    setRefreshPending(true);
    try {
      const next = await account.mutate();
      const action = pendingRef.current;
      if (action && next?.authenticated === true) await resume(action);
    } catch {
      // SWR owns the error state rendered by the checkpoint. Keep focus-driven
      // refreshes from producing an unhandled rejection while offline.
    } finally {
      refreshing.current = false;
      setRefreshPending(false);
    }
  }, [account, resume]);

  useEffect(() => {
    if (!pending || account.data?.authenticated === true) return;
    const refreshOnFocus = () => void refresh();
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, [account.data?.authenticated, pending, refresh]);

  const requireAccount = useCallback(
    (intent: DurableAccountIntent, run: PendingAction["run"]) => {
      if (accountReady && account.data?.authenticated === true) {
        void run();
        return true;
      }
      const action = { id: ++nextActionId.current, intent, run };
      pendingRef.current = action;
      setPending(action);
      return false;
    },
    [account.data?.authenticated, accountReady],
  );

  const cancel = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
  }, []);

  return {
    authenticated: accountReady && account.data?.authenticated === true,
    ready: accountReady,
    error: account.error,
    requireAccount,
    checkpoint: pending ? (
      <DurabilityAccountCheckpoint
        intent={pending.intent}
        loading={account.isLoading || account.isValidating || refreshPending}
        unavailable={Boolean(account.error)}
        onCancel={cancel}
        onRetry={() => void refresh()}
      />
    ) : null,
  };
}

function DurabilityAccountCheckpoint({
  intent,
  loading,
  unavailable,
  onCancel,
  onRetry,
}: {
  intent: DurableAccountIntent;
  loading: boolean;
  unavailable: boolean;
  onCancel: () => void;
  onRetry: () => void;
}): ReactNode {
  const copy = INTENT_COPY[intent];
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-[420px]" data-account-checkpoint={intent}>
        <DialogHeader>
          <DialogTitle>{unavailable ? "Account status unavailable" : copy.title}</DialogTitle>
          <DialogDescription>
            {unavailable
              ? "Excellent could not confirm who is signed in, so no retained or shared state was changed."
              : copy.detail}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {unavailable ? (
            <Button type="button" disabled={loading} onClick={onRetry}>
              {loading ? "Checking account" : "Retry account status"}
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={onCancel}>Keep using locally</Button>
              <Button
                type="button"
                disabled={loading}
                onClick={() => window.open("/login", "excellent-account", "popup,width=560,height=760,noopener")}
              >
                {loading ? "Checking account" : "Sign in or create account"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
